import { CardContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useIsWorkplaceReviewUXUpdateEnabled } from "@src/appV2/PostShiftReview/hooks/useIsWorkplaceReviewUXUpdateEnabled";
import {
  Card,
  HorizontalScrollView,
  ReviewsSummary,
  Text,
  Title,
} from "@src/appV2/redesign/system/ui-components";
import { useGetRatingAggregates } from "@src/appV2/redesign/Workplace/Review/api/useGetRatingAggregates";
import { type PaginatedWorkplaceCommentsParams } from "@src/appV2/redesign/Workplace/Review/api/usePaginatedWorkplaceComments";
import {
  useWorkplaceComments,
  WORKPLACE_COMMENTS_PAGE_SIZE,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { CommentReactionSource } from "@src/appV2/redesign/Workplace/Review/types";
import { useHistory } from "react-router-dom";

import { LEAVE_REVIEW_ENTRYPOINT } from "../../../PostShiftReview/types";
import { Button } from "../../components/Button";
import {
  SHIFT_DISCOVERY_UNLOCK_REVIEWS_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_LEAVE_REVIEW_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_REVIEWS_COMMENT_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_REVIEWS_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useWorkplaceModalsDataContext } from "../../ShiftDiscovery/useWorkplaceModalsDataContext";
import { WorkplaceHighlightPills } from "../Highlights/Pills";
import { useWorkplaceHighlights } from "../Highlights/useWorkplaceHighlights";
import { useHideReviewsUIByFacilityId } from "../hooks/useHideReviewsUIByFacilityId";
import { WorkplaceReviewCommentCompactCardContainer } from "../Review/Comment/CompactCardContainer";
import { useUpdateCachedComment } from "../Review/Comment/useUpdateCachedComment";
import { PatientLoadSection } from "../Review/PatientLoadSection";
import { RatingSummary } from "../Review/RatingSummary";
import { UNLOCK_REVIEWS_ORIGIN } from "../Review/UnlockReviews/navigation";
import { useCanLeaveReview } from "../Review/useCanLeaveReview";
import { useDelayedWorkplaceReviewFlag } from "../Review/useDelayedWorkplaceReviewFlag";
import { isReviewRedacted } from "../Review/utils";

export interface WorkplaceHighlightsAndReviewsCardProps {
  workplaceId: string;
  workplaceType: string | undefined;
  placementId?: string;
}

export function WorkplaceHighlightsAndReviewsCard(props: WorkplaceHighlightsAndReviewsCardProps) {
  const { workplaceId, workplaceType, placementId } = props;

  const { useDelayedReviewUI } = useDelayedWorkplaceReviewFlag();
  const { isEnabled: isReviewUXUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();

  const history = useHistory();

  const { generateModalPath, reviewsViewContext } = useWorkplaceModalsDataContext();

  const commentsParams: PaginatedWorkplaceCommentsParams = {
    workplaceId,
    page: { size: WORKPLACE_COMMENTS_PAGE_SIZE },
    filter: isReviewUXUpdateEnabled ? { sort: "-relevancyScore" } : undefined,
    viewContext: reviewsViewContext,
  };

  const { data: ratingAggregates, isSuccess: ratingAggregatesIsSuccess } =
    useGetRatingAggregates(workplaceId);

  const { highlights, isLoading: isHighlightsLoading } = useWorkplaceHighlights({
    workplaceId,
    includePatientRatioHighlights: !useDelayedReviewUI,
  });

  const { facilityIds } = useHideReviewsUIByFacilityId();

  const showComments = !facilityIds.includes(workplaceId);

  const { data: comments, isSuccess: commentsIsSuccess } = useWorkplaceComments(commentsParams);

  const { updateComment } = useUpdateCachedComment({
    workplaceId,
    commentsParams,
  });

  const { canLeaveReview } = useCanLeaveReview({
    workplaceId,
    workplaceType,
    source: LEAVE_REVIEW_ENTRYPOINT.WORKPLACE_PROFILE,
  });

  function handleUnlockPress() {
    logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_UNLOCK_CTA_TAPPED, { workplaceId });
    history.push(generateModalPath(SHIFT_DISCOVERY_UNLOCK_REVIEWS_MODAL_PATH, { workplaceId }), {
      unlockReviewsOrigin: UNLOCK_REVIEWS_ORIGIN.WORKPLACE_PROFILE,
    });
  }

  if (!ratingAggregatesIsSuccess || (showComments && !commentsIsSuccess)) {
    return null;
  }

  const { avgRating, numberOfRatings } = ratingAggregates;

  return (
    <Card>
      <CardContent sx={{ padding: 7 }}>
        <Stack spacing={4}>
          <Title bold variant="h5" component="h5">
            Reviews
          </Title>
          <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
            Reported by Clipboard professionals from past shifts. This information is not verified
            by the workplace and conditions may have changed.
          </Text>
          {useDelayedReviewUI ? (
            <RatingSummary rating={avgRating ?? 0} ratingsCount={numberOfRatings ?? 0} />
          ) : (
            <ReviewsSummary rating={avgRating ?? 0} ratingsCount={numberOfRatings ?? 0} />
          )}
        </Stack>

        {useDelayedReviewUI && <PatientLoadSection workplaceId={workplaceId} />}

        {highlights.length > 0 && (
          <>
            <Text semibold variant="body1" sx={{ marginTop: 8, marginBottom: 7 }}>
              Highlights
            </Text>

            <WorkplaceHighlightPills highlights={highlights} isLoading={isHighlightsLoading} />
          </>
        )}

        {showComments && (
          <>
            <HorizontalScrollView stretch sx={{ marginTop: 8 }} outerPaddingCompensation={7}>
              {comments?.map((comment) => (
                <WorkplaceReviewCommentCompactCardContainer
                  key={comment.id}
                  comment={comment}
                  workplaceId={workplaceId}
                  reactionSource={CommentReactionSource.WORKPLACE_PROFILE}
                  showEngagementActions={isReviewUXUpdateEnabled}
                  isLocked={isReviewRedacted(comment)}
                  onUnlockPress={() => handleUnlockPress()}
                  onLikeChanged={(comment) => {
                    updateComment(comment);
                  }}
                  onClick={() => {
                    history.push(
                      generateModalPath(SHIFT_DISCOVERY_WORKPLACE_REVIEWS_COMMENT_MODAL_PATH, {
                        workplaceId,
                        commentId: comment.id,
                      })
                    );
                  }}
                />
              ))}
            </HorizontalScrollView>

            {(comments?.length ?? 0) > 0 ? (
              <Button
                fullWidth
                variant="outlined"
                size="medium"
                sx={{ marginTop: 8 }}
                href={generateModalPath(SHIFT_DISCOVERY_WORKPLACE_REVIEWS_MODAL_PATH, {
                  workplaceId,
                })}
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_SCREEN_OPENED, {
                    workplaceId,
                    trigger: "Workplace Profile Modal",
                    isReviewUXUpdateEnabled,
                    ...(placementId && { placementId }),
                  });
                }}
              >
                {(comments?.length ?? 0) === 1
                  ? "View 1 review"
                  : `View all ${(comments?.length ?? 0) >= WORKPLACE_COMMENTS_PAGE_SIZE ? `${WORKPLACE_COMMENTS_PAGE_SIZE}+` : (comments?.length ?? 0)} reviews`}
              </Button>
            ) : (
              <Button
                fullWidth
                variant="outlined"
                size="medium"
                sx={{ marginTop: 8 }}
                href={generateModalPath(SHIFT_DISCOVERY_WORKPLACE_LEAVE_REVIEW_MODAL_PATH, {
                  workplaceId,
                })}
                disabled={!canLeaveReview}
              >
                {canLeaveReview ? "Leave a Review" : "No written reviews yet"}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
